import {
  ResaleListingFormFields,
  ResaleListingSubmitFormFields,
} from "@/modules/property-tracks/utils";
import { useEffect, useMemo, useState } from "react";
import PropertyDetailsForm from "@/modules/property-tracks/components/track-actions/add-track-detailed/property-details-form";
import PriceDetailsForm from "@/modules/property-tracks/components/track-actions/add-track-detailed/price-details/price-details-form";
import ResaleDocumentsForm from "@/modules/property-tracks/components/listing-tab/resale-listing/forms/documents-form";

import ListingDescriptionForm from "@/modules/property-tracks/components/listing-tab/common/description-form";
import useResaleListingForms from "./resale-forms.hook";

interface ResaleListingMultiStepFormProps {
  handleSubmit: (data: ResaleListingSubmitFormFields) => void;
  defaultStep?: number;
  defaultValues: ResaleListingFormFields;
  trackUid: string;
}

function useResaleListingMultiStepForm({
  handleSubmit,
  defaultStep = 0,
  defaultValues,
  trackUid,
}: ResaleListingMultiStepFormProps) {
  const [activeStep, setActiveStep] = useState<number>(defaultStep);

  useEffect(() => {
    if (defaultStep !== undefined) {
      setActiveStep(defaultStep);
    }
  }, [defaultStep]);

  const nextStep = () => {
    setActiveStep((prev) => (steps.length - 1 === prev ? prev : prev + 1));
  };

  const previousStep = () => {
    setActiveStep((prev) => (prev === 0 ? prev : prev - 1));
  };

  const {
    propertyDetailsForm,
    propertyPriceForm,
    resaleDocumentsForm,
    listingDescriptionForm,

    validate,
    reset,
  } = useResaleListingForms({ defaultValues });

  const submitAllForms = async (publish: boolean) => {
    // Choose validation based on publish mode
    const { isValid, firstErrorStep } = await validate(publish);
    if (!isValid) {
      if (firstErrorStep !== null) {
        setActiveStep(firstErrorStep);
      }
      return;
    }

    const preparedData: ResaleListingSubmitFormFields = {
      propertyDetails: propertyDetailsForm.getValues(),
      propertyPriceDetails: propertyPriceForm.getValues(),
      resaleDocumentsDetails: resaleDocumentsForm.getValues(),
      listingDescriptionDetails: listingDescriptionForm.getValues(),
      publish,
    };

    handleSubmit(preparedData);
  };

  const handlePublish = () => submitAllForms(true);
  const handleSave = () => submitAllForms(false);

  const steps = useMemo(
    () => [
      {
        value: 0,
        title: "Unit",
        onNext: propertyDetailsForm.handleSubmit(() => nextStep()),
        content: <PropertyDetailsForm form={propertyDetailsForm} />,
      },
      {
        value: 1,
        title: "Payment",
        onNext: propertyPriceForm.handleSubmit(() => nextStep()),
        content: (
          <PriceDetailsForm form={propertyPriceForm} extraFields={true} />
        ),
      },
      {
        value: 2,
        title: "Media",
        onNext: resaleDocumentsForm.handleSubmit(() => nextStep()),
        content: (
          <ResaleDocumentsForm form={resaleDocumentsForm} trackUid={trackUid} />
        ),
      },
      {
        value: 3,
        title: "Description",
        onNext: listingDescriptionForm.handleSubmit(() => nextStep()),
        content: <ListingDescriptionForm form={listingDescriptionForm} />,
      },
    ],
    [
      propertyDetailsForm,
      propertyPriceForm,
      resaleDocumentsForm,
      listingDescriptionForm,
      trackUid,
    ]
  );

  const currentStep = steps.find((s) => s.value === activeStep) ?? steps[0];

  const clearForms = () => {
    setActiveStep(defaultStep);
    reset();
  };

  return {
    activeStep,
    steps,
    nextStep,
    previousStep,
    currentStep,
    isLastStep: activeStep === steps[steps.length - 1].value,
    reset,
    clearForms,
    handlePublish,
    handleSave,
    forms: {
      propertyDetails: propertyDetailsForm,
      propertyPrice: propertyPriceForm,
      resaleDocuments: resaleDocumentsForm,
    },
  };
}

export default useResaleListingMultiStepForm;
